import type { Ref } from 'vue'

// 支付方式枚举
export enum PaymentMethodEnum {
  PAYPAL = 3, // PayPal 支付
  STRIPE = 4, // Stripe 支付
}

/**
 * 支付方式初始化和展示的通用规则
 *
 * 规则 1: PayPal 优先 → hasPayPalPasswordlessPayment === true 时优先展示 PayPal
 *   - 兜底：如果 PayPal 商品为空，则展示 paymentMethod=4 (Stripe) 的商品
 * 规则 2: 有订单历史 → hasPlacedOrder === true 时展示所有支付方式
 * 规则 3: 新用户默认 → 只展示 defaultDisplay = 1 的支付方式
 * 规则 4: 兜底策略 → 若无 defaultDisplay = 1 的支付方式，展示所有支付方式
 */

/**
 * 初始化支付方式
 */
export function initializePaymentMethod(
  currentPaymentMethod: Ref<number>,
  productLists: Ref<any[]>,
  hasPayPalPasswordlessPayment: Ref<boolean>,
  hasPlacedOrder: Ref<boolean>,
) {
  // 规则 1: PayPal 优先
  if (hasPayPalPasswordlessPayment.value) {
    // 检查是否有 PayPal 商品
    const paypalLists = productLists.value.filter(list => list.paymentMethod === PaymentMethodEnum.PAYPAL)
    if (paypalLists.length > 0) {
      currentPaymentMethod.value = PaymentMethodEnum.PAYPAL
    }
    else {
      // 兜底：使用 Stripe
      const stripeLists = productLists.value.filter(list => list.paymentMethod === PaymentMethodEnum.STRIPE)
      currentPaymentMethod.value = stripeLists[0]?.paymentMethod || PaymentMethodEnum.PAYPAL
    }
    return
  }
  if (hasPlacedOrder.value) {
    // 规则 2: 有订单历史，默认选择第一个支付方式
    currentPaymentMethod.value = productLists.value[0]?.paymentMethod || PaymentMethodEnum.PAYPAL
    return
  }

  // 规则 3: 根据订单历史和 defaultDisplay 确定
  const defaultPaymentMethods = productLists.value.filter(
    list => list.defaultDisplay === 1,
  )

  if (defaultPaymentMethods.length > 0) {
    // 选择第一个默认展示的支付方式
    currentPaymentMethod.value = defaultPaymentMethods[0].paymentMethod
  }
  else {
    // 规则 4: 兜底：选择第一个支付方式
    currentPaymentMethod.value = productLists.value[0]?.paymentMethod || PaymentMethodEnum.PAYPAL
  }
}

/**
 * 创建可用的支付方式列表 computed
 * @param excludeStripe 是否排除 Stripe（充值页面需要排除）
 */
export function createAvailablePaymentMethods(
  productLists: Ref<any[]>,
  hasPayPalPasswordlessPayment: Ref<boolean>,
  hasPlacedOrder: Ref<boolean>,
) {
  return () => {
    const lists = productLists.value

    // 规则 1: PayPal 无密码支付优先
    if (hasPayPalPasswordlessPayment.value) {
      const paypalLists = lists.filter(list => list.paymentMethod === PaymentMethodEnum.PAYPAL)

      // 兜底：如果 PayPal 商品为空，则展示 Stripe 商品
      if (paypalLists.length === 0) {
        const stripeLists = lists.filter(list => list.paymentMethod === PaymentMethodEnum.STRIPE)
        return stripeLists
      }

      return paypalLists
    }

    // 规则 2: 有订单历史
    if (hasPlacedOrder.value)
      return lists

    // 规则 3: 新用户默认展示
    const defaultDisplayLists = lists.filter(list => list.defaultDisplay === 1)

    if (defaultDisplayLists.length > 0)
      return defaultDisplayLists

    // 规则 4: 兜底：如果没有 defaultDisplay=1 的商品（即都为 0），展示所有商品
    return lists
  }
}

/**
 * 获取支付方式名称
 */
export function getPaymentChannelName(paymentMethod: number): string {
  return paymentMethod === PaymentMethodEnum.PAYPAL ? 'paypal' : 'stripe'
}

/**
 * 切换支付方式
 */
export function createSelectPaymentMethod(currentPaymentMethod: Ref<number>) {
  return (method: number) => {
    currentPaymentMethod.value = method
  }
}
